<template>
    <Container height="screen" :padded="false">
        <Head>
            <Title>{{ product?.seo?.title ?? product?.name }}</Title>
            <Meta
                name="description"
                :content="
                    product?.seo?.description ||
                    t('product.seo.fallback_description', {
                        name: product?.name || t('product.seo.fallback_title'),
                    })
                "
            />
        </Head>
        <div class="max-lg:px-2 h-full group">
            <div class="pt-12 3xl:pt-(--header-height)">
                <Glass v-gsap.desktop.parallax.slower-15 padding="md" class="z-20 relative w-full lg:w-1/2 lg:min-w-lg" content-class="flex-col">
                    <CreateBreadcrumb :list="breadcrumbs" :max-items="isDesktop ? 5 : 3" />
                    <Separator class="my-4" />
                    <Typography tag="h1" variant="h1" class="my-2 text-foreground">{{ product?.name }}</Typography>
                </Glass>
                <div class="relative place-items-center grid mx-auto w-full max-w-2xl 2xl:max-w-3xl aspect-square">
                    <div class="top-1/2 absolute h-full aspect-square -translate-y-1/2">
                        <PageProductCarousel
                            :gallery="{ ...product?.feature_image, ...product?.gallery }"
                            class="top-1/2 left-1/2 z-10 absolute w-full lg:w-3/4 max-w-2xl h-full lg:h-3/4 max-h-168 -translate-x-1/2 -translate-y-1/2"
                        />
                        <div class="absolute inset-0 max-lg:overflow-hidden">
                            <div
                                v-gsap.parallax.faster-2
                                class="-top-1/11 lg:-top-1/4 -left-1/11 lg:-left-1/6 absolute w-5/4 lg:w-4/3 h-5/4 lg:h-4/3"
                            >
                                <WidgetBlob svg-class="fill-primary/5 z-0" width="100%" height="100%" :seed />
                            </div>
                            <Glass
                                v-if="isDesktop"
                                v-gsap.parallax.faster-2
                                padding="md"
                                class="max-lg:hidden bottom-1/2 left-0 z-10 absolute w-1/4 max-w-28 -translate-x-1/2! translate-y-1/2"
                                content-class="flex-col"
                            >
                                <div v-for="(item, key) in mainAttributes" :key :loading="pending">
                                    <PageProductDetailCard :title="item.title" :image="item.image" />
                                    <Separator v-if="!(key + 1 === mainAttributes.length)" class="my-2" />
                                </div>
                            </Glass>
                        </div>

                        <div v-gsap.parallax.faster-10 class="absolute bottom-0 left-1/5">
                            <div class="opacity-30 group-hover:opacity-85 transition-opacity group-hover:animate-jello-horizontal">
                                <Glass class="p-2">
                                    <Typography variant="subtitle2" class="max-md:text-[.65rem] text-primary">
                                        {{ $t("general.zoom.notification") }}
                                    </Typography>
                                </Glass>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Separator class="my-12" />
            <div v-if="pending" />
            <div v-else>
                <Glass v-if="isMobile" padding="md" class="lg:hidden mt-24 w-full" content-class="gap-4 justify-around">
                    <div v-for="(item, key) in mainAttributes" :key class="flex-1">
                        <PageProductDetailCard :title="item.title" :image="item.image" class="mx-auto max-w-20" />
                        <Separator v-if="!(key + 1 === mainAttributes.length) && isDesktop" class="my-2" />
                    </div>
                </Glass>
                <div
                    class="relative flex max-lg:flex-col justify-between mb-16"
                    :class="includes?.length && stoneList.length ? 'separator-vertical' : ''"
                >
                    <div v-if="stoneList.length" :class="includes?.length ? 'lg:w-5/12 grow-0' : 'w-full lg:w-1/2 grow mx-auto'">
                        <WidgetSectionTitle :title="t('product.stone_list')" tag="h3" class="text-2xl lg:text-4xl" />
                        <PageProductDetailSketchList
                            :items="stoneList"
                            :class="includes?.length ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 lg:grid-cols-6 place-items-center'"
                        />
                    </div>
                    <div v-if="includes?.length" :class="stoneList?.length ? 'lg:w-5/12 grow-0' : 'w-full lg:w-1/2 grow mx-auto'">
                        <WidgetSectionTitle :title="t('product.includes')" tag="h3" class="text-2xl lg:text-4xl" />
                        <div
                            class="gap-4 grid"
                            :class="stoneList?.length ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 lg:grid-cols-6 place-items-center'"
                        >
                            <PageProductDetailCard
                                v-for="(item, key) in includes"
                                :key
                                :title="{
                                    value: $t(`product.type.${String(item).trim().toLowerCase()}`),
                                }"
                                :image="getProductTypeImage(item)"
                                class="flex-1 p-4 w-full shrink-0"
                            />
                        </div>
                    </div>
                </div>
                <div class="pb-16">
                    <WidgetSectionTitle :title="t('product.attributes')" tag="h3" class="text-2xl lg:text-4xl mb-6" />
                    <div v-if="extraAttributes?.length" class="rounded-xl border border-border bg-surface/80">
                        <Table class="w-full text-sm">
                            <TableHeader>
                                <TableRow>
                                    <TableHead class="text-left rtl:text-right py-3 px-4">{{ t("product.attribute.label") }}</TableHead>
                                    <TableHead class="text-left rtl:text-right py-3 px-4">{{ t("product.value") }}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <TableRow v-for="attribute in extraAttributes" :key="attribute.name" class="odd:bg-muted/10">
                                    <TableCell class="py-3 px-4">{{ t(`product.attribute.${attribute.name}`, attribute.name) }}</TableCell>
                                    <TableCell class="py-3 px-4">{{ getAttributeValue(attribute) }}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </div>
                    <div v-else class="text-sm text-muted mt-4">{{ t("product.no_attributes") }}</div>
                </div>
            </div>
        </div>
    </Container>
</template>

<script lang="ts" setup>
import { CreateBreadcrumb } from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Glass } from "@/components/ui/glass";
import type { BreadcrumbItem } from "~/components/ui/breadcrumb/CreateBreadcrumb.vue";
import { getColorTranslation } from "~/utils/get-color-translation";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";

const { t } = useI18n();
const { isDesktop, isMobile } = device();
const route = useRoute();
const slug: string = (Array.isArray(route.params.slug) ? route.params.slug?.[0] : route.params.slug) ?? "";

const { data: product, pending } = useProductDetail({ slug });

// OG Image for Product page
const ogTitle = computed(() => product?.value?.seo?.title || product?.value?.name || t("product.seo.fallback_title"));
const ogDescription = computed(
    () =>
        product?.value?.seo?.description ||
        t("product.seo.fallback_description", {
            name: product?.value?.name || t("product.seo.fallback_title"),
        }),
);
const ogImage = computed(() => getSingleImage(product?.value?.feature_image)?.src);
defineOgImageComponent("Product", {
    title: ogTitle.value,
    description: ogDescription.value,
    image: ogImage.value,
});

const seed = ref(generateRandomSeed());

const breadcrumbs: ComputedRef<BreadcrumbItem[]> = computed(() => {
    return [
        {
            label: t("general.home"),
            to: "/",
            icon: {
                name: "majesticons:home",
            },
        },
        {
            label: t("general.categories"),
            to: "/categories",
            icon: {
                name: "majesticons:tickets-line",
            },
        },
        {
            label: t(`category.name.${product?.value?.category?.name}`, product?.value?.category?.name ?? ""),
            to: `/categories/${product?.value?.category?.slug}`,
            icon: {
                name: product?.value?.category?.icon ?? "svg-spinners:bouncing-ball",
            },
        },
        {
            label: product?.value?.name ?? "",
            icon: {
                name: "hugeicons:delivery-box-02",
            },
        },
    ];
});

function parseShapes(input: string | undefined) {
    if (!input) return [];
    const fields = ["shape", "size", "count"];

    return input
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => item.split("_").map((p) => p.trim()))
        .map((parts) => fields.map((title, idx) => ({ title, value: parts[idx] ?? "" })));
}

const stoneList = computed(() => {
    return parseShapes(useFind(product?.value?.attributes, { name: "stones" })?.value);
});

const includes = computed(() => {
    return useFind(product?.value?.attributes, { name: "include" })?.value?.split(",");
});

const mainAttributes = computed(() => [
    {
        title: {
            title: "type",
            value: useFind(product?.value?.attributes, { name: "type" })?.value ?? "no value",
        },
        image: getProductTypeImage(useFind(product?.value?.attributes, { name: "type" })?.value),
    },
    {
        title: {
            title: "colors",
            value: getColorTranslation(useFind(product?.value?.attributes, { name: "colors" })?.value ?? "label", t),
        },
        image: "/images/sketches/colors.png",
    },
    {
        title: {
            title: "weight",
            value: (useFind(product?.value?.attributes, { name: "weight" })?.value ?? "no value") + "g",
        },
        image: "/images/sketches/scale.png",
    },
]);

const extraAttributes = computed(() => {
    const filterAttributes = ["type", "colors", "weight", "include", "stones"];
    return useFilter(product?.value?.attributes, (attr: APIProductAttribute) => {
        return !filterAttributes.includes(attr.name);
    });
});

function getAttributeValue(attr: APIProductAttribute) {
    if (attr.type === "boolean") return attr.value === "0" ? "✔️" : "⭕";
    else return t(`product.attr.value.${attr.value}`, attr.value);
}
</script>

<style scoped>
@reference "tailwindcss";

@variant lg {
    .separator-vertical::after {
        content: "";
        position: absolute;
        height: 80%;
        width: 1px;
        top: 10%;
        left: 50%;
        transform: translate(-50%, 0);
        background: var(--color-primary);
    }
}
</style>
